/**
 * 数据导出工具函数
 */

import * as XLSX from 'xlsx';
import { 
  City, 
  TrainingBusiness, 
  TrainingElement, 
  TrainingStandard,
  InspectionReport 
} from '../types';
import { EVALUATION_CONFIG } from './importTemplate';
import { CITY_LEVELS } from './constants';

export interface ExportData {
  id: string;
  batchId: string;
  companyName: string;
  businessName: string;
  elementName: string;
  actualValue: number;
  unit: string;
  cityLevel: number;
  responsiblePerson: string;
  checkDate: string;
  referenceRange: string;
  evaluation: {
    status: string;
    evaluation?: string;
  };
  remarks?: string;
  createTime: string;
}

/**
 * 导出城市数据为Excel
 */
export const exportCitiesToExcel = (cities: City[], filename: string = '城市数据.xlsx') => {
  const data = cities.map(city => ({
    'ID': city.id,
    '城市名称': city.name,
    '城市等级': `${city.level}级城市`,
    '创建时间': new Date(city.created_at).toLocaleDateString(),
    '更新时间': new Date(city.updated_at).toLocaleDateString(),
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, '城市数据');
  
  XLSX.writeFile(workbook, filename);
};

/**
 * 导出培训业务数据为Excel
 */
export const exportTrainingBusinessesToExcel = (businesses: TrainingBusiness[], filename: string = '培训业务数据.xlsx') => {
  const data = businesses.map(business => ({
    'ID': business.id,
    '业务名称': business.name,
    '业务描述': business.description || '',
    '创建时间': new Date(business.created_at).toLocaleDateString(),
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, '培训业务数据');
  
  XLSX.writeFile(workbook, filename);
};

/**
 * 导出培训要素数据为Excel
 */
export const exportTrainingElementsToExcel = (elements: TrainingElement[], filename: string = '培训要素数据.xlsx') => {
  const data = elements.map(element => ({
    'ID': element.id,
    '要素名称': element.name,
    '计量单位': element.unit,
    '要素描述': element.description || '',
    '创建时间': new Date(element.created_at).toLocaleDateString(),
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, '培训要素数据');
  
  XLSX.writeFile(workbook, filename);
};

/**
 * 导出培训标准数据为Excel
 */
export const exportTrainingStandardsToExcel = (standards: TrainingStandard[], filename: string = '培训标准数据.xlsx') => {
  const data = standards.map(standard => ({
    'ID': standard.id,
    '培训业务': standard.business_name || '',
    '培训要素': standard.element_name || '',
    '城市等级': standard.city_level ? `${standard.city_level}级城市` : '通用',
    '最小值': standard.min_range || '',
    '最大值': standard.max_range || '',
    '计量单位': standard.unit,
    '状态': standard.is_active === 1 ? '启用' : '停用',
    '备注': standard.remarks || '',
    '创建时间': new Date(standard.created_at).toLocaleDateString(),
    '更新时间': new Date(standard.updated_at).toLocaleDateString(),
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);
  
  // 设置列宽
  const colWidths = [
    { wch: 8 },   // ID
    { wch: 15 },  // 培训业务
    { wch: 15 },  // 培训要素
    { wch: 20 },  // 公司名称
    { wch: 12 },  // 负责人
    { wch: 12 },  // 城市等级
    { wch: 10 },  // 最小值
    { wch: 10 },  // 最大值
    { wch: 10 },  // 计量单位
    { wch: 8 },   // 状态
    { wch: 30 },  // 备注
    { wch: 12 },  // 创建时间
    { wch: 12 },  // 更新时间
  ];
  worksheet['!cols'] = colWidths;
  
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, '培训标准数据');
  
  XLSX.writeFile(workbook, filename);
};

/**
 * 导出检查报告数据到Excel
 */
export function exportInspectionReport(
  data: ExportData[], 
  filename: string = '检查报告数据'
): void {
  try {
    // 准备导出的数据
    const exportData = data.map((item, index) => {
      const config = EVALUATION_CONFIG[item.evaluation.status as keyof typeof EVALUATION_CONFIG];
      
      // 安全检查，确保config存在
      if (!config) {
        console.warn('❌ exportUtils: 找不到配置:', {
          status: item.evaluation.status,
          statusType: typeof item.evaluation.status,
          availableStatuses: Object.keys(EVALUATION_CONFIG)
        });
      }
      
      return [
        index + 1,                                                    // 序号
        item.batchId,                                                 // 批次号
        item.companyName,                                            // 业务所属创大
        item.businessName,                                           // 培训业务名称
        item.elementName,                                            // 培训要素
        `${item.actualValue} ${item.unit}`,                         // 本次检查结果
        item.checkDate,                                             // 检查时间
        CITY_LEVELS[item.cityLevel as keyof typeof CITY_LEVELS],   // 主体分级
        item.responsiblePerson,                                     // 负责人
        item.referenceRange,                                        // 参考范围
        config ? `${config.icon} ${config.text}` : '-',           // 提示
        item.evaluation.evaluation || '-',                         // 评定
        item.remarks || '-',                                        // 备注
        item.createTime                                             // 创建时间
      ];
    });

    // 添加表头
    const headers = [
      '序号',
      '批次号', 
      '业务所属创大',
      '培训业务名称',
      '培训要素',
      '本次检查结果',
      '检查时间',
      '主体分级',
      '负责人',
      '参考范围',
      '提示',
      '评定',
      '备注',
      '创建时间'
    ];

    const worksheetData = [headers, ...exportData];

    // 创建工作簿和工作表
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    // 设置列宽
    ws['!cols'] = [
      { width: 8 },   // 序号
      { width: 20 },  // 批次号
      { width: 15 },  // 业务所属创大
      { width: 15 },  // 培训业务名称
      { width: 12 },  // 培训要素
      { width: 15 },  // 本次检查结果
      { width: 15 },  // 检查时间
      { width: 12 },  // 主体分级
      { width: 10 },  // 负责人
      { width: 20 },  // 参考范围
      { width: 12 },  // 提示
      { width: 25 },  // 评定
      { width: 30 },  // 备注
      { width: 20 }   // 创建时间
    ];

    // 添加工作表到工作簿
    XLSX.utils.book_append_sheet(wb, ws, '检查报告');

    // 生成并下载文件
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const finalFilename = `${filename}_${timestamp}.xlsx`;
    
    XLSX.writeFile(wb, finalFilename);
    
    console.log(`导出成功: ${finalFilename}`);
  } catch (error) {
    console.error('导出失败:', error);
    throw new Error('导出失败: ' + (error as Error).message);
  }
}

/**
 * 导出统计汇总报告
 */
export function exportSummaryReport(data: ExportData[], filename: string = '检查报告统计'): void {
  try {
    // 统计数据
    const stats = {
      total: data.length,
      companies: new Set(data.map(d => d.companyName)).size,
      businesses: new Set(data.map(d => d.businessName)).size,
      elements: new Set(data.map(d => d.elementName)).size,
      cityLevels: {} as Record<number, number>,
      evaluationStats: {} as Record<string, number>
    };

    // 统计城市等级分布
    data.forEach(item => {
      stats.cityLevels[item.cityLevel] = (stats.cityLevels[item.cityLevel] || 0) + 1;
      stats.evaluationStats[item.evaluation.status] = (stats.evaluationStats[item.evaluation.status] || 0) + 1;
    });

    // 准备统计数据
    const summaryData = [
      ['统计项目', '数值', '说明'],
      ['总记录数', stats.total, '所有检查记录的总数'],
      ['涉及公司数', stats.companies, '参与检查的公司数量'],
      ['业务类型数', stats.businesses, '涉及的培训业务类型'],
      ['要素类型数', stats.elements, '涉及的培训要素类型'],
      [],
      ['城市等级分布', '', ''],
      ...Object.entries(stats.cityLevels).map(([level, count]) => [
        CITY_LEVELS[parseInt(level) as keyof typeof CITY_LEVELS],
        count,
        `占比 ${((count / stats.total) * 100).toFixed(1)}%`
      ]),
      [],
      ['评定结果分布', '', ''],
      ...Object.entries(stats.evaluationStats).map(([status, count]) => {
        const config = EVALUATION_CONFIG[status as keyof typeof EVALUATION_CONFIG];
        
        // 安全检查，确保config存在
        if (!config) {
          console.warn('❌ exportUtils stats: 找不到配置:', {
            status: status,
            statusType: typeof status,
            availableStatuses: Object.keys(EVALUATION_CONFIG)
          });
        }
        
        return [
          config ? `${config.icon} ${config.text}` : status,
          count,
          `占比 ${((count / stats.total) * 100).toFixed(1)}%`
        ];
      })
    ];

    // 创建工作簿
    const wb = XLSX.utils.book_new();
    
    // 添加统计汇总工作表
    const summaryWs = XLSX.utils.aoa_to_sheet(summaryData);
    summaryWs['!cols'] = [
      { width: 20 },
      { width: 15 },
      { width: 25 }
    ];
    XLSX.utils.book_append_sheet(wb, summaryWs, '统计汇总');

    // 添加详细数据工作表
    const detailData = data.map((item, index) => [
      index + 1,
      item.batchId,
      item.companyName,
      item.businessName,
      item.elementName,
      item.actualValue,
      item.unit,
      CITY_LEVELS[item.cityLevel as keyof typeof CITY_LEVELS],
      item.responsiblePerson,
      item.checkDate,
      item.referenceRange,
      item.evaluation.evaluation || '-',
      item.remarks || '-'
    ]);

    const detailHeaders = [
      '序号', '批次号', '公司名称', '业务名称', '培训要素', 
      '实际值', '单位', '城市等级', '负责人', '检查时间', 
      '参考范围', '评定结果', '备注'
    ];

    const detailWs = XLSX.utils.aoa_to_sheet([detailHeaders, ...detailData]);
    detailWs['!cols'] = Array(detailHeaders.length).fill({ width: 12 });
    XLSX.utils.book_append_sheet(wb, detailWs, '详细数据');

    // 生成并下载文件
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const finalFilename = `${filename}_${timestamp}.xlsx`;
    
    XLSX.writeFile(wb, finalFilename);
    
    console.log(`统计报告导出成功: ${finalFilename}`);
  } catch (error) {
    console.error('统计报告导出失败:', error);
    throw new Error('统计报告导出失败: ' + (error as Error).message);
  }
}

/**
 * 导出分组检查报告（支持智能单元格合并）
 */
export function exportGroupedInspectionReport(
  reports: InspectionReport[],
  filename: string = '分组检查报告'
): void {
  try {
    if (!reports || reports.length === 0) {
      throw new Error('没有可导出的数据');
    }

    // 按公司→业务→要素→参考范围多层级分组
    const companyGroups = reports.reduce((acc, report) => {
      const companyKey = report.company_name;
      if (!acc[companyKey]) {
        acc[companyKey] = {};
      }
      
      const businessKey = report.business_name;
      if (!acc[companyKey][businessKey]) {
        acc[companyKey][businessKey] = {};
      }
      
      const elementKey = report.element_name;
      if (!acc[companyKey][businessKey][elementKey]) {
        acc[companyKey][businessKey][elementKey] = {};
      }
      
      const referenceKey = report.reference_range || 'no-range';
      if (!acc[companyKey][businessKey][elementKey][referenceKey]) {
        acc[companyKey][businessKey][elementKey][referenceKey] = [];
      }
      
      acc[companyKey][businessKey][elementKey][referenceKey].push(report);
      return acc;
    }, {} as Record<string, Record<string, Record<string, Record<string, InspectionReport[]>>>>);

    // 创建工作簿
    const workbook = XLSX.utils.book_new();
    
    // 准备数据数组
    const data: any[][] = [];
    
    // 添加表头
    const headers = [
      '公司名称',
      '城市等级', 
      '负责人',
      '培训业务',
      '培训要素',
      '检查结果',
      '参考范围',
      '预警状态',
      '评定结果'
    ];
    data.push(headers);

    // 合并范围记录
    const merges: XLSX.Range[] = [];
    let currentRow = 1; // 从第二行开始（第一行是表头）

    // 处理多层级分组数据
    Object.entries(companyGroups).forEach(([companyName, businesses]) => {
      const businessEntries = Object.entries(businesses);
      let companyRowCount = 0;
      
      // 计算公司的总行数
      businessEntries.forEach(([, elements]) => {
        Object.entries(elements).forEach(([, references]) => {
          Object.entries(references).forEach(([, reports]) => {
            companyRowCount += reports.length;
          });
        });
      });
      
      const companyStartRow = currentRow;
      
      businessEntries.forEach(([businessName, elements]) => {
        const elementEntries = Object.entries(elements);
        let businessRowCount = 0;
        
        // 计算业务的总行数
        elementEntries.forEach(([, references]) => {
          Object.entries(references).forEach(([, reports]) => {
            businessRowCount += reports.length;
          });
        });
        
        const businessStartRow = currentRow;
        
        elementEntries.forEach(([elementName, references]) => {
          const referenceEntries = Object.entries(references);
          let elementRowCount = 0;
          
          // 计算要素的总行数
          referenceEntries.forEach(([, reports]) => {
            elementRowCount += reports.length;
          });
          
          const elementStartRow = currentRow;
          
          referenceEntries.forEach(([referenceRange, reports]) => {
            const referenceRowCount = reports.length;
            const referenceStartRow = currentRow;
            
            reports.forEach((report) => {
              // 添加数据行
              const statusMap = {
                'normal': '✅ 正常',
                'warning': '⚠️ 预警', 
                'danger': '🚨 异常'
              };
              
              const row = [
                companyName,                    // 公司名称
                `${report.city_level}级城市`,    // 城市等级
                report.responsible_person,       // 负责人
                businessName,                   // 培训业务
                elementName,                    // 培训要素
                `¥${report.actual_value.toLocaleString()}`, // 检查结果
                referenceRange === 'no-range' ? '-' : referenceRange, // 参考范围
                statusMap[report.warning_status as keyof typeof statusMap] || report.warning_status, // 预警状态
                report.evaluation || '-'         // 评定结果
              ];
              
              data.push(row);
              currentRow++;
            });
            
            // 添加参考范围合并
            if (referenceRowCount > 1) {
              merges.push({
                s: { r: referenceStartRow, c: 6 }, // 参考范围列开始
                e: { r: referenceStartRow + referenceRowCount - 1, c: 6 } // 参考范围列结束
              });
            }
          });
          
          // 添加培训要素合并
          if (elementRowCount > 1) {
            merges.push({
              s: { r: elementStartRow, c: 4 }, // 培训要素列开始
              e: { r: elementStartRow + elementRowCount - 1, c: 4 } // 培训要素列结束
            });
          }
        });
        
        // 添加业务合并范围
        if (businessRowCount > 1) {
          merges.push({
            s: { r: businessStartRow, c: 3 }, // 培训业务列开始
            e: { r: businessStartRow + businessRowCount - 1, c: 3 } // 培训业务列结束
          });
        }
      });
      
      // 添加公司级别的合并范围
      if (companyRowCount > 1) {
        // 公司名称列合并
        merges.push({
          s: { r: companyStartRow, c: 0 },
          e: { r: companyStartRow + companyRowCount - 1, c: 0 }
        });
        
        // 城市等级列合并
        merges.push({
          s: { r: companyStartRow, c: 1 },
          e: { r: companyStartRow + companyRowCount - 1, c: 1 }
        });
        
        // 负责人列合并
        merges.push({
          s: { r: companyStartRow, c: 2 },
          e: { r: companyStartRow + companyRowCount - 1, c: 2 }
        });
      }
    });

    // 创建工作表
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    
    // 设置合并单元格
    if (merges.length > 0) {
      worksheet['!merges'] = merges;
    }

    // 设置列宽
    const colWidths = [
      { wch: 20 }, // 公司名称
      { wch: 12 }, // 城市等级
      { wch: 12 }, // 负责人
      { wch: 16 }, // 培训业务
      { wch: 16 }, // 培训要素
      { wch: 16 }, // 检查结果
      { wch: 24 }, // 参考范围
      { wch: 12 }, // 预警状态
      { wch: 32 }  // 评定结果
    ];
    worksheet['!cols'] = colWidths;

    // 为表头设置样式
    for (let col = 0; col < headers.length; col++) {
      const cellAddress = XLSX.utils.encode_cell({ c: col, r: 0 });
      if (!worksheet[cellAddress]) worksheet[cellAddress] = {};
      
      worksheet[cellAddress].s = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "4472C4" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } }
        }
      };
    }

    // 添加工作表到工作簿
    XLSX.utils.book_append_sheet(workbook, worksheet, '分组检查报告');

    // 生成文件名并导出
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const finalFilename = `${filename}_${timestamp}.xlsx`;
    
    XLSX.writeFile(workbook, finalFilename);
    
    console.log(`分组报告导出成功: ${finalFilename}`);
  } catch (error) {
    console.error('分组报告导出失败:', error);
    throw new Error('分组报告导出失败: ' + (error as Error).message);
  }
}

/**
 * 通用确认对话框
 */
export const showConfirmDialog = (message: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const result = window.confirm(message);
    resolve(result);
  });
};